import Icons from '@/const/icons';
import {
	AdsCampaignTabs,
	Campaign,
	CampaignChannels,
	CampaignTabs,
	CampaignTabsDetails,
} from '@/interfaces';

export type ExtendedCampaignChannels = CampaignChannels | 'GOOGLE';

export const campaignPlatformIconMap: Record<ExtendedCampaignChannels, string> =
	{
		FACEBOOK: Icons.facebook2,
		INSTAGRAM: Icons.instagram,
		MARKETING_EMAIL: Icons.envelope2,
		GOOGLE: Icons.google2,
		GOOGLE_ADS: Icons.google2,
		GOOGLE_SEARCH: Icons.google2,
		FACEBOOK_ADS: Icons.facebook2,
		INSTAGRAM_ADS: Icons.instagram,
	};

export const campaignPlatformLabelMap: Record<CampaignChannels, string> = {
	FACEBOOK: 'Facebook',
	INSTAGRAM: 'Instagram',
	MARKETING_EMAIL: 'Email',
	GOOGLE_ADS: 'Google Ads',
	GOOGLE_SEARCH: 'Google Search',
	FACEBOOK_ADS: 'Facebook Ads',
	INSTAGRAM_ADS: 'Instagram Ads',
};

export const getChannelsByCampaignDetails = (
	campaignDetails: Campaign
): CampaignChannels[] => {
	if (!campaignDetails || !campaignDetails.tabs) return [];
	const channelsTypes = Object.values(campaignDetails.tabs).reduce(
		(acc, currentValue: CampaignTabsDetails[]) => {
			currentValue.forEach((item) => {
				if (item && 'type' in item) {
					acc.push(item.type);
				}
			});
			return acc;
		},
		[]
	);
	return channelsTypes;
};

export const getAllAdsArray = (
	tabDetails?: CampaignTabs
): CampaignTabsDetails[] | undefined => {
	if (!tabDetails) return;
	const adsCreated: CampaignTabsDetails[] = Object.values(tabDetails).flatMap(
		(item) => item
	);
	return adsCreated;
};

export const getHasAdsCreated = (campaignDetails?: Campaign): boolean => {
	if (!campaignDetails || !campaignDetails.tabs) return false;

	const adsCreatedArray = getAllAdsArray(campaignDetails.tabs);
	if (!adsCreatedArray) return false;

	const hasAds = adsCreatedArray.some((item) => item.ads.length > 0);
	return hasAds;
};

export const getHasAdsScheduled = (campaignDetails?: Campaign): boolean => {
	if (!campaignDetails || !campaignDetails.tabs) return false;

	const adsCreatedArray = getAllAdsArray(campaignDetails.tabs);
	if (!adsCreatedArray) return false;

	const hasScheduled = adsCreatedArray.some((item) =>
		item.ads.some((ad) => ad.start_date || ad.end_date)
	);
	return hasScheduled;
};

export const getIsTabApproved = (
	tabDetails?: CampaignTabsDetails[]
): boolean | undefined => {
	if (!tabDetails) return;
	const approved = tabDetails.some((item) => item.approved);
	return approved;
};

export const getAd = (
	type: CampaignChannels,
	tabDetails?: CampaignTabs
): AdsCampaignTabs | undefined => {
	const adsCreatedArray = getAllAdsArray(tabDetails);
	if (!adsCreatedArray) return;

	const ad = adsCreatedArray.find((item) => item.type === type)?.ads[0];
	return ad;
};

export const getIsAdApproved = (
	tabDetails: CampaignTabsDetails[],
	type: CampaignChannels
): boolean => {
	// if (!tabDetails) return;
	const approved = tabDetails.some(
		(item) => item.type === type && item.approved
	);
	return approved;
};

export const getChannelId = (
	tabDetails: CampaignTabsDetails[],
	type: CampaignChannels
): string | undefined => {
	const id = tabDetails.find((item) => item.type === type)?.id;
	return id;
};

export const getAspectRatioByChannel: Record<CampaignChannels, number> = {
	FACEBOOK: 4 / 5,
	FACEBOOK_ADS: 4 / 5,
	GOOGLE_ADS: 16 / 9,
	GOOGLE_SEARCH: 16 / 9,
	INSTAGRAM: 1 / 1,
	INSTAGRAM_ADS: 1 / 1,
	MARKETING_EMAIL: 16 / 9, // by default (to email not use)
};

export const getImageSizeByChannel: Record<
	CampaignChannels,
	{ width: number; height: number }
> = {
	FACEBOOK: { height: 551, width: 0 }, // width: 0 = width: '100%'
	FACEBOOK_ADS: { height: 551, width: 0 },
	GOOGLE_ADS: { height: 0, width: 0 },
	GOOGLE_SEARCH: { height: 0, width: 0 },
	INSTAGRAM: { height: 551, width: 650 },
	INSTAGRAM_ADS: { height: 551, width: 650 },
	MARKETING_EMAIL: { height: 0, width: 0 }, // by default (to email not use)
};

export const adsArray: CampaignChannels[] = [
	'FACEBOOK_ADS',
	'INSTAGRAM_ADS',
	'GOOGLE_ADS',
	'GOOGLE_SEARCH',
];
